import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em) => { setEmail(em); setPassword(em === "info@3bldn.com" ? "3bldn-atelier-2026" : "3bldn2026"); };

  const isPreview = typeof window !== "undefined" && (
    window.location.hostname.includes("preview.emergentagent") ||
    window.location.hostname === "localhost" ||
    window.location.hostname.startsWith("127.")
  );

  return (
    <div className="min-h-screen grain flex flex-col lg:flex-row bg-[#0D0D0E]">
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden border-r hairline">
        <img src="https://images.unsplash.com/photo-1609619742069-f5e18afeef17?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Jewellery atelier" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(13,13,14,0.4),rgba(13,13,14,0.92))" }} />
        <div className="relative z-10 px-16 max-w-lg">
          <div className="eyebrow mb-4">Hatton Garden · London</div>
          <h1 className="font-serif text-6xl leading-[0.95] text-[#F5F3EF] mb-6">Bonded<br />by Bullion</h1>
          <p className="text-[#C2BEB5] text-lg leading-relaxed font-serif italic">More than a workspace.</p>
          <div className="mt-10 flex flex-wrap gap-x-3 gap-y-1 eyebrow">
            <span>People</span><span className="brass-text">·</span><span>Pieces</span><span className="brass-text">·</span>
            <span>Process</span><span className="brass-text">·</span><span>Progress</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-sm rise">
          <div className="mb-10">
            <Logo className="h-10" />
            <div className="eyebrow text-[0.55rem] mt-2.5 pl-0.5">3B OS · Member Portal</div>
          </div>

          <h2 className="font-serif text-3xl mb-1.5">Sign in</h2>
          <p className="text-sm text-[#8C867A] mb-7">Access your bench, jobs and workshop.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full panel rounded-sm px-3.5 py-3 text-sm outline-none focus:border-[#C5A059] transition-colors" placeholder="you@3bldn.com" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full panel rounded-sm px-3.5 py-3 text-sm outline-none focus:border-[#C5A059] transition-colors" placeholder="••••••••" />
            </div>
            {err && <div data-testid="login-error" className="text-sm px-3 py-2 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.1)" }}>{err}</div>}
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="btn-brass w-full py-3 rounded-sm text-sm">{loading ? "Signing in…" : "Enter Portal"}</button>
          </form>

          {isPreview && (
          <div className="mt-8 pt-6 border-t hairline">
            <div className="eyebrow mb-3">Demo access</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => quick("info@3bldn.com")} data-testid="demo-admin"
                className="panel rounded-sm px-3 py-2.5 text-left hover:border-[#C5A059] transition-colors">
                <div className="text-xs text-[#E2DFD7]">Admin</div>
                <div className="text-[0.6rem] text-[#8C867A] truncate">info@3bldn.com</div>
              </button>
              <button onClick={() => quick("marcus@3bldn.com")} data-testid="demo-member"
                className="panel rounded-sm px-3 py-2.5 text-left hover:border-[#C5A059] transition-colors">
                <div className="text-xs text-[#E2DFD7]">Bench Holder</div>
                <div className="text-[0.6rem] text-[#8C867A] truncate">marcus@3bldn.com</div>
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
